import React, { useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Loader } from "@googlemaps/js-api-loader";

const GooglePlacesAutocomplete = ({ onPlaceSelected, placeholder, helperText }) => {
    const containerRef = useRef(null);
    const autocompleteRef = useRef(null);

    useEffect(() => {
        const initAutocomplete = async () => {
            try {
                const loader = new Loader({
                    apiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
                    version: "weekly",
                    libraries: ["places"]
                });
                await loader.importLibrary('places');

                if (containerRef.current && !autocompleteRef.current) {
                    // Create the element
                    const inputElement = document.createElement('input');
                    inputElement.placeholder = placeholder;
                    inputElement.style.width = '100%';
                    inputElement.style.height = '56px';
                    inputElement.style.fontFamily = '"Roboto", "Helvetica", "Arial", sans-serif';
                    inputElement.style.fontSize = '1rem';
                    inputElement.style.padding = '16.5px 14px';
                    inputElement.style.borderRadius = '4px';
                    inputElement.style.border = '1px solid rgba(0, 0, 0, 0.23)';

                    // Initialize the autocomplete
                    const autocomplete = new window.google.maps.places.PlaceAutocompleteElement();
                    autocomplete.setAttribute('placeholder', placeholder);

                    autocompleteRef.current = autocomplete;

                    // Append the input element to the container
                    containerRef.current.appendChild(autocomplete);

                    autocomplete.addEventListener('gmp-select', async ({ placePrediction  }) => {
                        const place = placePrediction.toPlace();

                        if (typeof place.fetchFields === 'function') {
                            await place.fetchFields({
                                fields: ['displayName', 'formattedAddress', 'location', 'addressComponents']
                            }).catch(err => console.error("Error fetching place fields:", err));
                        } else {
                            console.error("place.fetchFields is not a function");
                            // Handle cases where place object might be different than expected
                            onPlaceSelected({ formatted_address: place.displayName || 'Error fetching details' });
                            return;
                        }

                        let city = '';
                        let region = '';
                        let country = '';

                        // Extract address components
                        place.addressComponents?.forEach(component => {
                            const types = component.types;
                            if (types.includes('locality')) city = component.longText;
                            else if (types.includes('administrative_area_level_1')) region = component.longText;
                            else if (types.includes('country')) country = component.shortText;
                        });

                        onPlaceSelected({
                            city,
                            region,
                            country,
                            formatted_address: place.formatted_address
                        });
                    });

                    autocompleteRef.current = autocomplete;
                }
            } catch (error) {
                console.error('Error initializing Google Places autocomplete:', error);
            }
        };

        initAutocomplete();

        return () => {
            if (containerRef.current && containerRef.current.firstChild) {
                containerRef.current.removeChild(containerRef.current.firstChild);
            }
            autocompleteRef.current = null;
        };
    }, []);

    return (
        <Box>
            <Box
                ref={containerRef}
                sx={{
                    position: 'relative',
                    mb: 2,
                    '& input': {
                        transition: 'border-color 0.15s, padding 0.15s, background-color 0.15s',
                    },
                }}
            />
            <Typography
                variant="caption"
                sx={{
                    display: 'block',
                    mt: 0.5,
                    ml: 1.75,
                    color: 'text.secondary'
                }}
            >
                {helperText}
            </Typography>
        </Box>
    );
};

export default GooglePlacesAutocomplete;